import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { isEqual, get, head, isEmpty } from 'lodash';
import { LoadingLocalTakeover } from 'web-ui-components/lib/organisms/takeovers';
import { WebUIThemeProvider } from 'web-ui-components/lib/utilities/themes';

import FasterPaymentsApp from '../fasterPayments/components/App';
import BacsApp from '../bacs/components/App/App';
import IFTApp from '../ift/components/App/App';
import ChapsApp from '../chaps/components/App/App';
import { TYPE_FASTER_PAYMENTS, TYPE_BACS_CREDIT, TYPE_BACS_DEBIT, TYPE_IFT, TYPE_CHAPS } from '../config';
import createStore from '../store';
import { setAccounts } from '../states/accounts/actions';
import { setUser } from '../states/user/actions';
import { goToPaymentsLanding, getLandingPage } from '../states/page/actions';
import {
	setGroupApprovalsStatus,
	setIsAuthorisationEdit,
	setPaymentType,
	setIsLCV,
	setCanAddMultiplePayees,
} from '../states/modes/actions';
import { setValidationType } from '../states/validation/actions';
import { getAllBeneficiaries, GET_ALL_BENEFICIARIES_SUCCESS } from '../states/payees/actions';

import accountPropTypes from '../states/accounts/propTypes';
import sessionPropTypes from '../states/session/propTypes';
import envConfigPropTypes from '../states/envConfig/propTypes';
import { getCustomerCapabilities } from '../states/limits/actions';
import { editIFTPayment } from '../states/ift/actions';
import { editCHAPSPayment } from '../states/chaps/actions';
import { getFavouriteAccounts, GET_FAVOURITE_ACCOUNTS_SUCCESS } from '../states/favourites/actions';
import { LARGE_CUSTOMER_MIN_ACCOUNTS_SIZE } from '../pickerLayout/constants';
import { getMissingAccountsAndBensForEditPayments } from '../utils/LCVMissingDetailsUtils';

const editPayment = ({ paymentType, dispatch, paymentToEdit, batchDetails, payments }) => {
	switch (paymentType) {
		case TYPE_IFT:
			return dispatch(editIFTPayment(paymentToEdit));
		case TYPE_CHAPS:
			return dispatch(editCHAPSPayment({ paymentToEdit, batchDetails, payments }));
		default:
			return null;
	}
};

class PaymentAppSelector extends Component {
	constructor(props) {
		super(props);
		const {
			session,
			envConfig,
			hasPaymentCapability,
			multiTransactions,
			isAuthorisationEdit,
			user,
			paymentType,
			isManagePaymentEdit,
			paymentToEdit,
			isAuthorisationEditById,
			hasGroupApprovalsEnabled = false,
			envConfig: { fileUpload },
		} = this.props;

		this.store = createStore({
			session,
			envConfig,
			modes: {
				hasGroupApprovalsEnabled,
				hasPaymentCapability,
				multiTransactions,
				isManagePaymentEdit,
				isAuthorisationEdit,
				isAuthorisationEditById,
			},
			user,
			page: paymentToEdit ? '' : getLandingPage(paymentType),
			features: { fileUpload },
		});
		const { dispatch } = this.store;
		dispatch(setIsAuthorisationEdit(isAuthorisationEdit));
		dispatch(setValidationType({ paymentType: paymentType || TYPE_FASTER_PAYMENTS }));
		this.handleSelectPaymentType = this.handleSelectPaymentType.bind(this);
		dispatch(setPaymentType({ paymentType: paymentType || TYPE_FASTER_PAYMENTS }));
		dispatch(setGroupApprovalsStatus(hasGroupApprovalsEnabled));
		this.state = { paymentType: paymentType || TYPE_FASTER_PAYMENTS, isBootStrapInProgress: true };
	}

	getChildContext() {
		const { fetchParentBeneficiary = () => {} } = this.props;
		return { fetchParentBeneficiary };
	}

	componentDidMount() {
		const { dispatch, getState } = this.store;
		const { handleRoute, paymentType, handleAccountsForNonLCVUser } = this.props;
		const {
			session,
			user: { customers = [] },
			paymentToEdit = {},
			paymentDetails,
			payments,
			isManagePaymentEdit,
			isAuthorisationEdit,
		} = this.props;
		const customerId = get(head(customers), 'customer_id');
		Promise.all([
			dispatch(getCustomerCapabilities(session.bankId, customerId)),
			dispatch(getFavouriteAccounts(session.bankId)),
		])
			.then((responses) => {
				if (responses.some((response) => response.error)) {
					handleRoute('WEB-ERROR');
				}

				const getFavouriteAccountsResponse = responses.find(
					(response) => response.type === GET_FAVOURITE_ACCOUNTS_SUCCESS
				);
				const totalNumberOfAccounts = get(getFavouriteAccountsResponse, 'payload.total_number_of_accounts', 0);
				const isLCV = totalNumberOfAccounts >= LARGE_CUSTOMER_MIN_ACCOUNTS_SIZE;
				const { batchDetails = {} } = paymentDetails || {};
				dispatch(setIsLCV(isLCV));
				dispatch(setCanAddMultiplePayees(true));
				if (isLCV && !isEmpty(paymentToEdit)) {
					return editPayment({ paymentType, dispatch, paymentToEdit, batchDetails, payments });
				}
				if (isLCV && (isManagePaymentEdit || isAuthorisationEdit) && payments.length) {
					dispatch(
						getMissingAccountsAndBensForEditPayments({
							state: getState(),
							payments,
							paymentToEdit,
						})
					).then(() => {
						editPayment({ paymentType, dispatch, paymentToEdit, batchDetails, payments });
					});
				}

				return dispatch(getAllBeneficiaries(session.bankId)).then((response) => {
					if (response.type === GET_ALL_BENEFICIARIES_SUCCESS) {
						dispatch(setAccounts(handleAccountsForNonLCVUser()));
						if (paymentToEdit || batchDetails) {
							return editPayment({
								paymentType,
								dispatch,
								paymentToEdit,
								batchDetails,
								payments,
							});
						}
					}
					return null;
				});
			})
			.then(() => this.setState({ isBootStrapInProgress: false }))
			.catch(() => handleRoute('WEB-ERROR'));
	}
	componentDidUpdate(prevProps) {
		const { accounts, user, isAuthorisationEdit, paymentType } = this.props;

		if (!isEqual(accounts, prevProps.accounts)) {
			this.store.dispatch(setAccounts(accounts));
		}

		if (!isEqual(user, prevProps.user)) {
			this.store.dispatch(setUser(user));
		}

		if (!isEqual(isAuthorisationEdit, prevProps.isAuthorisationEdit)) {
			this.store.dispatch(setIsAuthorisationEdit(isAuthorisationEdit));
		}

		if (!isEqual(paymentType, prevProps.paymentType)) {
			this.store.dispatch(setPaymentType({ paymentType }));
		}
	}

	handleSelectPaymentType(paymentType) {
		this.setState({ paymentType });
		this.store.dispatch(setValidationType({ paymentType: paymentType || TYPE_FASTER_PAYMENTS }));
		this.store.dispatch(goToPaymentsLanding({ paymentType }));
		this.store.dispatch(setPaymentType({ paymentType }));
	}

	renderApp() {
		const { paymentType, isBootStrapInProgress } = this.state;
		switch (paymentType) {
			case TYPE_FASTER_PAYMENTS:
				return (
					<FasterPaymentsApp
						handleSelectPaymentType={this.handleSelectPaymentType}
						isBootStrapInProgress={isBootStrapInProgress}
						{...this.props}
					/>
				);
			case TYPE_BACS_CREDIT:
				return (
					<BacsApp
						paymentType={TYPE_BACS_CREDIT}
						handleSelectPaymentType={this.handleSelectPaymentType}
						isBootStrapInProgress={isBootStrapInProgress}
						{...this.props}
					/>
				);
			case TYPE_BACS_DEBIT:
				return (
					<BacsApp
						paymentType={TYPE_BACS_DEBIT}
						handleSelectPaymentType={this.handleSelectPaymentType}
						isBootStrapInProgress={isBootStrapInProgress}
						{...this.props}
					/>
				);
			case TYPE_IFT:
				return (
					<IFTApp
						{...this.props}
						handleSelectPaymentType={this.handleSelectPaymentType}
						isBootStrapInProgress={isBootStrapInProgress}
					/>
				);
			case TYPE_CHAPS:
				return (
					<ChapsApp
						{...this.props}
						handleSelectPaymentType={this.handleSelectPaymentType}
						isBootStrapInProgress={isBootStrapInProgress}
					/>
				);
			default:
				return null;
		}
	}

	render() {
		return (
			<Provider store={this.store}>
				<LoadingLocalTakeover show={this.state.isBootStrapInProgress} title="loading...">
					<div className="web-ui-components">{this.renderApp()}</div>
				</LoadingLocalTakeover>
			</Provider>
		);
	}
}
PaymentAppSelector.propTypes = {
	accounts: PropTypes.arrayOf(PropTypes.shape(accountPropTypes).isRequired),
	copCheckTriggered: PropTypes.func,
	session: PropTypes.shape(sessionPropTypes).isRequired,
	envConfig: PropTypes.shape(envConfigPropTypes).isRequired,
	paymentType: PropTypes.string,
	hasGroupApprovalsEnabled: PropTypes.bool,
	hasPaymentCapability: PropTypes.bool.isRequired,
	multiTransactions: PropTypes.bool,
	isAuthorisationEdit: PropTypes.bool,
	isManagePaymentEdit: PropTypes.bool,
	isLargeCustomerView: PropTypes.bool,
	user: PropTypes.shape({ id: PropTypes.string }),
	handleRoute: PropTypes.func.isRequired,
	isAuthorisationEditById: PropTypes.bool,
	paymentToEdit: PropTypes.shape({ paymentId: PropTypes.string, account: PropTypes.string }),
	paymentDetails: PropTypes.shape({
		batchDetails: PropTypes.shape({
			batchId: PropTypes.string.isRequired,
			isBatch: PropTypes.bool.isRequired,
			batchName: PropTypes.string.isRequired,
		}),
	}),
	payments: PropTypes.shape({}),
	handleAccountsForNonLCVUser: PropTypes.func.isRequired,
	fetchParentBeneficiary: PropTypes.func,
	sessionCOPChecks: PropTypes.object,
};

PaymentAppSelector.childContextTypes = {
	fetchParentBeneficiary: PropTypes.func,
};

export default PaymentAppSelector;
